import { getClient } from "@retreats/supabase-adapter";
import { STORAGE_BUCKETS } from "../constants";

export type BetaChecklist = {
  loginEasy: boolean;
  checkoutEasy: boolean;
  foundRetreat: boolean;
  completedRetreat: boolean;
  // null when completedRetreat is false
  audioWorked: boolean | null;
  appStayedOn: boolean | null;
  feltOriented: boolean | null;
  retreatWorthIt: boolean | null;
  retreatExperienceScore: number | null;
  wouldDoAgain: boolean | null;
};

export type FeedbackData = {
  userId: string;
  wave: string;
  betaChecklist: BetaChecklist;
  easeOfUseScore: number;
  trustScore: number;
  confusionText: string;
  positiveFeedback: string;
  additionalNotes: string;
  contactPermission: boolean;
  audioBlob?: Blob;
  screenshots: File[];
  logFile?: File;
};

// Returns the storage path on success, null on failure.
// Callers store the path, not a URL — signed URLs are generated at display time.
async function uploadFile(
  bucket: string,
  userId: string,
  file: Blob | File,
  extension: string,
): Promise<string | null> {
  const path = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${extension}`;
  const { error } = await getClient().storage.from(bucket).upload(path, file);
  return error ? null : path;
}

// Structured log path: {userId}/trace_{YYYY-MM-DD}_{wave}_{ts}.json
// Enables `feedback_submissions WHERE log_bundle_url LIKE '{userId}/trace_%'` admin queries.
async function uploadLogFile(
  bucket: string,
  userId: string,
  wave: string,
  file: File,
): Promise<string | null> {
  const date = new Date().toISOString().slice(0, 10);
  const safeWave = wave.replace(/[^a-z0-9-]/gi, "-");
  const path = `${userId}/trace_${date}_${safeWave}_${Date.now()}.json`;
  const { error } = await getClient().storage.from(bucket).upload(path, file);
  return error ? null : path;
}

export async function submitFeedback(data: FeedbackData): Promise<{ error?: string }> {
  const db = getClient();

  // Upload attachments — failures are non-fatal and never block text submission.
  const audioPath = data.audioBlob
    ? await uploadFile(STORAGE_BUCKETS.FEEDBACK_AUDIO, data.userId, data.audioBlob, "webm").catch(() => null)
    : null;

  const screenshotPaths: string[] = [];
  for (const file of data.screenshots) {
    const ext = file.name.split(".").pop() ?? "jpg";
    const path = await uploadFile(STORAGE_BUCKETS.FEEDBACK_SCREENSHOTS, data.userId, file, ext).catch(() => null);
    if (path) screenshotPaths.push(path);
  }

  // Log files use a structured name for retrieval: trace_{date}_{wave}_{ts}.json
  // This lets admins query by wave+date without needing a full DB scan.
  const logPath = data.logFile
    ? await uploadLogFile(STORAGE_BUCKETS.FEEDBACK_LOGS, data.userId, data.wave, data.logFile).catch(() => null)
    : null;

  const insertData = {
    user_id: data.userId,
    wave: data.wave,
    beta_checklist: data.betaChecklist,
    ease_of_use_score: data.easeOfUseScore,
    trust_score: data.trustScore,
    confusion_text: data.confusionText || null,
    positive_feedback: data.positiveFeedback || null,
    additional_notes: data.additionalNotes || null,
    contact_permission: data.contactPermission,
    audio_url: audioPath,
    screenshot_urls: screenshotPaths,
    log_bundle_url: logPath,
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error } = await db.from("feedback_submissions").insert(insertData as any);

  if (error) return { error: error.message };
  return {};
}

export type FeedbackRow = {
  id: string;
  wave: string;
  created_at: string;
  ease_of_use_score: number;
  trust_score: number;
  confusion_text: string;
  positive_feedback: string | null;
  contact_permission: boolean;
  audio_url: string | null;
  screenshot_urls: string[];
  log_bundle_url: string | null;
};

export type WaveStat = {
  wave: string;
  submissions: number;
  avgEase: number;
  avgTrust: number;
};

export async function fetchAdminFeedback(): Promise<{
  waveStats: WaveStat[];
  feedback: FeedbackRow[];
  error?: string;
}> {
  const db = getClient();
  const { data: rows, error } = await db
    .from("feedback_submissions")
    .select("*")
    .order("created_at", { ascending: false });

  if (error) return { waveStats: [], feedback: [], error: error.message };

  const allRows: FeedbackRow[] = rows ?? [];
  const byWave = new Map<string, FeedbackRow[]>();
  for (const r of allRows) {
    const key = r.wave ?? "unknown";
    if (!byWave.has(key)) byWave.set(key, []);
    byWave.get(key)!.push(r);
  }

  const waveStats: WaveStat[] = [...byWave.entries()].map(([wave, waveRows]) => ({
    wave,
    submissions: waveRows.length,
    avgEase: waveRows.reduce((s, r) => s + r.ease_of_use_score, 0) / waveRows.length,
    avgTrust: waveRows.reduce((s, r) => s + r.trust_score, 0) / waveRows.length,
  }));

  return { waveStats, feedback: allRows };
}
